import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  Optional,
  QueryList,
  Self,
} from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, NgControl, ValidationErrors } from '@angular/forms';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { interval, Observable } from 'rxjs';
import { SimpleField } from '../../abstracts/simple-field';
import { AnexConfig, AnexType } from '../../models/anex-config.model';
import { Loading, TemplateDirective } from '@fleet-beat/core';

@Component({
  selector: 'fb-number-field',
  templateUrl: './number-field.component.html',
  styleUrls: ['./number-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberFieldComponent extends SimpleField<number | string> implements OnInit {
  @Input() minlength: number;
  @Input() maxlength: number;
  @Input() min: number;
  @Input() max: number;
  @Input() prefix: AnexConfig;
  @Input() suffix: AnexConfig;
  @Input() digitsCount: number = 0;
  @Input() useCommaSeparator: boolean = true;
  @Input() loading: Loading = new Loading();
  @Input() templates: QueryList<TemplateDirective<object>>;

  numberControl: FormControl = new FormControl(undefined, { asyncValidators: [this.validateNumberControl(this)] });

  get prefixText(): string {
    return Boolean(this.prefix) && this.prefix.type === AnexType.Text ? this.prefix.text : '';
  }

  constructor(
    protected override changeDetectorRef: ChangeDetectorRef,
    @Optional()
    @Self()
    public override ngControl: NgControl,
  ) {
    super(changeDetectorRef, ngControl);
  }

  ngOnInit(): void {
    this.initListeners();
  }

  isEmpty(value: number | string): boolean {
    return value === undefined || value === null || value === '' || Number.isNaN(value);
  }

  protected override onWriteValue(value: number | string): void {
    if (!this.isEmpty(value)) {
      this.numberControl.setValue(typeof value === 'string' ? parseFloat(value) : value);
    } else {
      this.numberControl.setValue(undefined);
      this.numberControl.reset();
      this.numberControl.setErrors(null);
    }
  }

  private validateNumberControl(component: NumberFieldComponent): AsyncValidatorFn {
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return interval(1).pipe(
        take(1),
        map(() =>
          !component.ngControl?.control || component.ngControl.control.valid
            ? null
            : component.ngControl.control.errors,
        ),
      );
    };
  }

  private initListeners(): void {
    this.numberControl.valueChanges
      .pipe(
        untilComponentDestroyed(this),
        map((value: number | string) => (typeof value === 'string' ? Number(value) : value)),
        map((value: number) => (this.isEmpty(value) ? undefined : value)),
        distinctUntilChanged(),
      )
      .subscribe((value: number) => {
        this.value = value;
      });
  }
}
